import {
  DocumentReference,
  ISoftDelete,
  ITimestampRange,
  Timestamp,
} from '@principle-theorem/shared';
import { IPractice } from '../practice/practice';
import { ISubscriptionAddOn } from '../stripe/add-on';
import { IStripeInvoice } from '../stripe/invoice';
import { BillingMeterId, IBillingMeter } from './metered-billing';

export enum OrganisationSubscriptionCollection {
  StripeInvoices = 'stripeInvoices',
}

export interface IOrganisationSubscription extends ISoftDelete {
  subscriptionId: string;
  customerId: string;
  practiceRefs: DocumentReference<IPractice>[];
  meteredBillingEnabled?: boolean;
  meteredBillingEnabledAt?: Timestamp;
  billingMeters?: IBillingMeter[];
  /**
   * Snapshot of every add-on-bearing product on this subscription.
   * Written by the webhook (`StripeSubscriptionEvents`), and the cron.
   * Read by Angular gates (`AddOnService`) and the offline
   * `addOnsForOrganisation` consumer API.
   */
  addOns?: ISubscriptionAddOn[];
  /**
   * Latest Stripe `event.created` we have processed for this subscription.
   * Used by `customer.subscription.updated`/`deleted` handlers to detect
   * out-of-order delivery (`event.created < lastStripeEventCreatedAt` →
   * skip derivation, mark processed). Paired with `lastStripeEventId` so
   * logs can attribute the skip back to a specific event.
   */
  lastStripeEventCreatedAt?: Timestamp;
  /** Companion to {@link lastStripeEventCreatedAt} — id of the last applied event. */
  lastStripeEventId?: string;
}

export interface IStripeMeterUsage {
  meterId: BillingMeterId;
  units: number;
  cost: number;
  costPerUnit: number;
  practiceRef: DocumentReference<IPractice>;
}

export interface IStripeAccountStatus {
  period: ITimestampRange;
  meterUsage: IStripeMeterUsage[];
  meterUsageTotal: number;
  marketingUsageTotal: number;
  credit: number;
  balance: number;
  marketingBalance: number;
}

export interface IGetStripeAccountStatsRequest {
  subscriptionRef: DocumentReference<IOrganisationSubscription>;
}

export interface IGetStripeAccountStatsResponse {
  accountStatus: IStripeAccountStatus;
}

export interface ICreateDepositInvoiceRequest {
  subscriptionRef: DocumentReference<IOrganisationSubscription>;
  amount: number;
}

export interface ICreateDepositInvoiceResponse {
  invoiceRef: DocumentReference<IStripeInvoice>;
}

export interface ICancelDepositInvoiceRequest {
  invoiceRef: DocumentReference<IStripeInvoice>;
}
